// src/components/settings/EmbeddableFormSettings.js
import React, { useState, useMemo } from 'react';
import { Copy, Check } from 'lucide-react';

const EmbeddableFormSettings = ({ currentUserProfile }) => {
    const [copied, setCopied] = useState(false);
    const organizationId = currentUserProfile?.organizationId || 'YOUR_ORGANIZATION_ID_HERE'; // Fallback just in case

    // Use useMemo to generate the snippet only when organizationId changes
    const codeSnippet = useMemo(() => {
        // --- This is the HTML/JS snippet from before ---
        return `<div id="crm-booking-form-container">
  <form id="crm-booking-form" style="max-width: 500px; margin: 20px auto; padding: 20px; border: 1px solid #ccc; border-radius: 8px; font-family: sans-serif;">
    <h3 style="text-align: center; margin-bottom: 15px;">Request a Booking</h3>
    
    <input type="hidden" id="crm-organizationId" value="${organizationId}"> 
    
    <div style="margin-bottom: 10px;">
      <label for="crm-customerName" style="display: block; margin-bottom: 5px;">Name:</label>
      <input type="text" id="crm-customerName" required style="width: 100%; padding: 8px; border: 1px solid #ccc; border-radius: 4px;">
    </div>
    
    <div style="margin-bottom: 10px;">
      <label for="crm-customerEmail" style="display: block; margin-bottom: 5px;">Email:</label>
      <input type="email" id="crm-customerEmail" required style="width: 100%; padding: 8px; border: 1px solid #ccc; border-radius: 4px;">
    </div>
    
    <div style="margin-bottom: 10px;">
      <label for="crm-customerPhone" style="display: block; margin-bottom: 5px;">Phone (Optional):</label>
      <input type="tel" id="crm-customerPhone" style="width: 100%; padding: 8px; border: 1px solid #ccc; border-radius: 4px;">
    </div>
    
    <div style="margin-bottom: 10px;">
      <label for="crm-serviceType" style="display: block; margin-bottom: 5px;">Service/Subject (Optional):</label>
      <input type="text" id="crm-serviceType" style="width: 100%; padding: 8px; border: 1px solid #ccc; border-radius: 4px;">
    </div>
    
    <div style="margin-bottom: 15px;">
      <label for="crm-description" style="display: block; margin-bottom: 5px;">Description of Request:</label>
      <textarea id="crm-description" rows="4" required style="width: 100%; padding: 8px; border: 1px solid #ccc; border-radius: 4px; resize: vertical;"></textarea>
    </div>
    
    <button type="submit" id="crm-submit-button" style="width: 100%; padding: 10px; background-color: #007bff; color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 16px;">Submit Request</button>
    
    <div id="crm-form-message" style="margin-top: 10px; font-weight: bold; text-align: center;"></div>
  </form>
</div>

<script>
  (function() { // Wrap in IIFE to avoid global scope pollution
    const crmForm = document.getElementById('crm-booking-form');
    const crmMessageDiv = document.getElementById('crm-form-message');
    const crmSubmitButton = document.getElementById('crm-submit-button');
    const crmFunctionUrl = 'https://us-central1-smb-suite-platform.cloudfunctions.net/submitBookingRequest'; // Your Cloud Function URL

    if (!crmForm) return; // Don't run if form isn't on the page

    crmForm.addEventListener('submit', function(event) {
      event.preventDefault(); 
      
      const formData = {
        organizationId: document.getElementById('crm-organizationId').value,
        customerName: document.getElementById('crm-customerName').value,
        customerEmail: document.getElementById('crm-customerEmail').value,
        customerPhone: document.getElementById('crm-customerPhone').value,
        serviceType: document.getElementById('crm-serviceType').value,
        description: document.getElementById('crm-description').value,
      };
      
      if (!formData.organizationId || formData.organizationId === 'YOUR_ORGANIZATION_ID_HERE') {
          crmMessageDiv.textContent = 'Error: Form configuration is incomplete.';
          crmMessageDiv.style.color = 'red';
          return;
      }
       if (!formData.customerName || !formData.customerEmail || !formData.description) {
          crmMessageDiv.textContent = 'Please fill out all required fields.';
          crmMessageDiv.style.color = 'red';
          return;
      }

      crmMessageDiv.textContent = 'Submitting...';
      crmMessageDiv.style.color = 'black';
      crmSubmitButton.disabled = true;

      fetch(crmFunctionUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          crmMessageDiv.textContent = 'Thank you! Your request has been submitted.';
          crmMessageDiv.style.color = 'green';
          crmForm.reset(); 
        } else {
          crmMessageDiv.textContent = 'Error: ' + (data.error || 'Could not submit request.');
          crmMessageDiv.style.color = 'red';
        }
      })
      .catch(error => {
        console.error('Error submitting CRM form:', error);
        crmMessageDiv.textContent = 'An unexpected error occurred. Please try again later.';
        crmMessageDiv.style.color = 'red';
      })
      .finally(() => { crmSubmitButton.disabled = false; });
    });
  })(); // End IIFE
<\/script> 
`;
        // --- End of snippet ---
    }, [organizationId]);

    const handleCopy = () => {
        navigator.clipboard.writeText(codeSnippet).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000); // Reset icon after 2 seconds
        }, (err) => {
            console.error('Failed to copy text: ', err);
            alert('Failed to copy code. Please copy manually.');
        });
    };

    return (
        <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Embeddable Booking Form</h2>
            <p className="text-sm text-gray-600 mb-4">
                Copy the code snippet below and paste it into the HTML of your website where you want the booking form to appear.
                It's pre-configured with your organization's ID. Your website visitors can then submit requests directly into your CRM.
            </p>
            <div className="relative bg-gray-100 p-4 rounded border border-gray-300 max-h-96 overflow-auto">
                <button
                    onClick={handleCopy}
                    className="absolute top-2 right-2 p-1.5 bg-gray-300 hover:bg-gray-400 rounded text-gray-700"
                    title="Copy code to clipboard"
                >
                    {copied ? <Check size={16} className="text-green-600" /> : <Copy size={16} />}
                </button>
                <pre><code className="text-sm language-html">{codeSnippet}</code></pre>
            </div>
            <p className="text-xs text-gray-500 mt-2">
                Note: You can customize the form's appearance using your website's CSS. The included styles are minimal defaults.
            </p>
        </div>
    );
};

export default EmbeddableFormSettings;